import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Handshake, Landmark, Mail, MapPin, Phone, Lock, UserRound, LoaderCircle, BookOpenCheck, GraduationCap, Bot, ClipboardCheck, ShieldCheck, Gift } from "lucide-react";
import { Logo, EASE } from "@/components/landing/Reveal";
import { API, useAuth, formatApiError } from "@/context/AuthContext";

const OBJECTIVES = ["Titre de séjour", "Naturalisation", "Examen civique", "Je ne sais pas encore"];

const SIDE_FEATURES = [
  { icon: BookOpenCheck, title: "Informations officielles", desc: "Des contenus fiables et sourcés par les organismes publics." },
  { icon: GraduationCap, title: "Formation et examen civique", desc: "Cours, quiz et examens blancs pour vous préparer efficacement." },
  { icon: Bot, title: "Assistant démarches IA", desc: "Posez vos questions et obtenez des réponses adaptées à votre situation." },
  { icon: ClipboardCheck, title: "Suivi de votre parcours", desc: "Conservez vos documents, échéances et démarches au même endroit." },
];

function Field({ icon: Icon, ...props }) {
  return (
    <div className="relative">
      <Icon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        {...props}
        className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
      />
    </div>
  );
}

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", objective: OBJECTIVES[0], address: "", postalCode: "", phone: "", city: "" });
  const [geo, setGeo] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (isLogin) return;
    if (!/^\d{5}$/.test(form.postalCode)) { setGeo(null); return; }
    let stale = false;
    axios.get(`${API}/geo/postal/${form.postalCode}`)
      .then(({ data }) => {
        if (stale) return;
        setGeo({ ok: true, ...data });
        const cities = data.cities || [];
        setForm((f) => ({ ...f, city: cities.length === 1 ? cities[0] : (cities.includes(f.city) ? f.city : "") }));
      })
      .catch(() => { if (!stale) setGeo({ ok: false }); });
    return () => { stale = true; };
  }, [form.postalCode, isLogin]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin
        ? { email: form.email, password: form.password }
        : { name: form.name, email: form.email, password: form.password, objective: form.objective,
            address: form.address, postal_code: form.postalCode, phone: form.phone,
            city: form.city || null, ref: refCode || null, accept_terms: acceptTerms };
      const { data } = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
      setUser(data);
      toast.success(isLogin ? `Ravi de vous revoir, ${data.name?.split(" ")[0]} !` : "Votre compte est créé. Bienvenue sur INTEGRA FRANCE !");
      navigate(["admin", "super_admin", "moderator"].includes(data.role) ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="auth-page">
      <div className="relative hidden overflow-hidden bg-bleu-night lg:block">
        <img
          src="/auth-side.jpg"
          alt="Un couple de nouveaux arrivants consulte une tablette devant une préfecture française"
          className="absolute inset-y-0 right-0 h-full w-[58%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-bleu-night via-bleu-night/90 to-bleu-night/10" />
        <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-bleu-accent via-white to-rouge" />
        <div className="relative flex h-full flex-col p-10 xl:p-12">
          <Link to="/"><Logo dark /></Link>

          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mt-10 font-display text-[2.6rem] font-extrabold leading-[1.12] text-white xl:text-5xl"
            data-testid="auth-side-headline"
          >
            S'informer.<br />Comprendre.<br />Se préparer.<br /><span className="text-bleu-accent">Agir.</span>
          </motion.h2>
          <motion.span
            initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
            className="mt-4 block h-1 w-14 origin-left rounded bg-rouge"
          />

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="mt-6 max-w-xs text-[15px] leading-relaxed text-white/75"
          >
            Rejoignez INTEGRA FRANCE et avancez dans votre parcours d'intégration avec deux assistants IA,
            des quiz et un accompagnement pas à pas.
          </motion.p>

          <ul className="mt-9 max-w-xs space-y-6" data-testid="auth-side-features">
            {SIDE_FEATURES.map((f, i) => (
              <motion.li
                key={f.title}
                initial={{ opacity: 0, x: -18 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.35 + i * 0.12, ease: EASE }}
                className="flex items-start gap-3.5"
              >
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm">
                  <f.icon className="h-4 w-4 text-white" />
                </span>
                <div>
                  <p className="text-sm font-bold text-white">{f.title}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-white/60">{f.desc}</p>
                </div>
              </motion.li>
            ))}
          </ul>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9, ease: EASE }}
            className="mt-auto flex max-w-sm items-start gap-3 rounded-2xl bg-white/95 p-4 shadow-2xl"
            data-testid="auth-side-privacy-card"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bleu-light">
              <ShieldCheck className="h-5 w-5 text-bleu" />
            </span>
            <div>
              <p className="text-sm font-bold text-slate-900">Vos données sont protégées</p>
              <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                INTEGRA FRANCE respecte la confidentialité et la sécurité de vos informations.
              </p>
            </div>
          </motion.div>
          <p className="mt-4 text-[11px] text-white/40">© 2026 INTEGRA FRANCE — Service indépendant, non affilié à l'administration française.</p>
        </div>
      </div>

      <div className="flex flex-col bg-slate-50/70 px-6 py-8 sm:px-12">
        <div className="flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors hover:text-bleu" data-testid="auth-back-home">
            <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
          </Link>
          <Link to="/" className="lg:hidden"><Logo compact /></Link>
        </div>

        <div className="flex flex-1 items-center justify-center py-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="card-integra w-full max-w-md p-8"
          >
            <h1 className="font-display text-2xl font-extrabold text-slate-900">
              {isLogin ? "Bon retour parmi nous" : "Créez votre compte"}
            </h1>
            <p className="mt-1.5 text-sm text-slate-500">
              {isLogin
                ? "Retrouvez votre parcours et vos assistants IA."
                : "Moins d'une minute pour commencer votre parcours."}
            </p>

            <div className="mt-6 grid grid-cols-2 rounded-xl bg-slate-100 p-1" role="tablist">
              <button
                role="tab"
                aria-selected={isLogin}
                onClick={() => navigate("/login")}
                className={`rounded-lg py-2.5 text-sm font-semibold transition-all ${isLogin ? "bg-white text-bleu shadow-sm" : "text-slate-500"}`}
                data-testid="auth-tab-login"
              >
                Connexion
              </button>
              <button
                role="tab"
                aria-selected={!isLogin}
                onClick={() => navigate("/signup")}
                className={`rounded-lg py-2.5 text-sm font-semibold transition-all ${!isLogin ? "bg-white text-bleu shadow-sm" : "text-slate-500"}`}
                data-testid="auth-tab-signup"
              >
                Inscription
              </button>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4">
              {!isLogin && refCode && (
                <p className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs font-semibold text-emerald-700" data-testid="auth-ref-banner">
                  <Gift className="mt-0.5 h-4 w-4 shrink-0" />
                  Vous avez été invité(e) par un membre INTEGRA FRANCE. Bienvenue !
                </p>
              )}
              {!isLogin && (
                <Field icon={UserRound} type="text" required placeholder="Nom complet" value={form.name} onChange={set("name")} data-testid="auth-name-input" />
              )}
              <Field icon={Mail} type="email" required placeholder="exemple@email.com" value={form.email} onChange={set("email")} data-testid="auth-email-input" />
              <Field
                icon={Lock} type="password" required minLength={isLogin ? 1 : 8}
                placeholder={isLogin ? "Mot de passe" : "Mot de passe (8 caractères min.)"}
                value={form.password} onChange={set("password")} data-testid="auth-password-input"
              />
              {isLogin && (
                <div className="text-right">
                  <Link to="/mot-de-passe-oublie" className="text-xs font-semibold text-bleu hover:underline" data-testid="auth-forgot-link">
                    Mot de passe oublié ?
                  </Link>
                </div>
              )}
              {!isLogin && (
                <>
                  <Field icon={MapPin} type="text" required minLength={5} placeholder="Adresse (n° et rue, ex : 12 rue de la Paix)" value={form.address} onChange={set("address")} data-testid="auth-address-input" />
                  <div className="grid grid-cols-2 gap-3">
                    <Field icon={Landmark} type="text" required inputMode="numeric" pattern="\d{5}" maxLength={5} placeholder="Code postal" value={form.postalCode} onChange={set("postalCode")} data-testid="auth-postal-input" />
                    <Field icon={Phone} type="tel" required placeholder="Téléphone" value={form.phone} onChange={set("phone")} data-testid="auth-phone-input" />
                  </div>
                  {geo?.ok && (geo.cities || []).length > 0 && (
                    <select
                      required
                      value={form.city}
                      onChange={set("city")}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
                      data-testid="auth-city-select"
                    >
                      <option value="" disabled>Choisissez votre ville</option>
                      {geo.cities.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                  {geo?.ok && (
                    <p className="flex items-start gap-2 rounded-xl bg-bleu-light px-4 py-2.5 text-xs font-semibold text-bleu" data-testid="auth-prefecture-hint">
                      <Landmark className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {form.city ? `${form.city} — ` : ""}{geo.name} ({geo.code}) — votre préfecture de rattachement : {geo.prefecture}
                    </p>
                  )}
                  {geo && !geo.ok && (
                    <p className="rounded-xl bg-rose-50 px-4 py-2.5 text-xs font-medium text-rose-700" data-testid="auth-postal-error">
                      Code postal non reconnu. Vérifiez votre saisie (5 chiffres, France).
                    </p>
                  )}
                  <p className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-[11px] leading-relaxed text-slate-500" data-testid="auth-address-why">
                    <strong className="text-slate-700">Pourquoi ces informations ?</strong> Votre adresse et votre code postal
                    permettent de pré-remplir vos modèles de courrier administratifs et de vous orienter vers la préfecture ou la
                    sous-préfecture compétente pour votre département. Votre téléphone sert aux rappels d'échéances importantes.
                  </p>
                </>
              )}
              {!isLogin && (
                <select
                  value={form.objective}
                  onChange={set("objective")}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
                  data-testid="auth-objective-select"
                >
                  {OBJECTIVES.map((o) => <option key={o} value={o}>Objectif : {o}</option>)}
                </select>
              )}

              {error && (
                <p className="rounded-xl border border-rouge/20 bg-rose-50 px-4 py-3 text-xs font-medium text-rose-700" data-testid="auth-error-message">
                  {error}
                </p>
              )}

              {!isLogin && (
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3" data-testid="auth-terms-label">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#1E3FAE]"
                    data-testid="auth-terms-checkbox"
                  />
                  <span className="text-[11px] leading-relaxed text-slate-600">
                    J'ai lu et j'accepte les{" "}
                    <Link to="/conditions" target="_blank" className="font-bold text-bleu underline" data-testid="auth-terms-link">Conditions d'utilisation</Link>{" "}
                    et la{" "}
                    <Link to="/confidentialite" target="_blank" className="font-bold text-bleu underline" data-testid="auth-privacy-link">Politique de confidentialité</Link>{" "}
                    (traitement de mes données personnelles conforme au RGPD). <span className="text-rouge">*</span>
                  </span>
                </label>
              )}
              <button type="submit" disabled={loading || (!isLogin && !acceptTerms)} className="btn-primary w-full disabled:opacity-60" data-testid="auth-submit-button">
                {loading && <LoaderCircle className="h-4 w-4 animate-spin" />}
                {isLogin ? "Se connecter" : "Créer mon compte"}
              </button>
              <p className="text-center text-xs text-slate-500" data-testid="auth-switch-hint">
                {isLogin ? (
                  <>Pas encore de compte ? <Link to="/signup" className="font-bold text-bleu hover:underline">Créer un compte</Link></>
                ) : (
                  <>Vous avez déjà un compte ? <Link to="/login" className="font-bold text-bleu hover:underline">Se connecter</Link></>
                )}
              </p>
            </form>

            <div className="mt-5 flex items-center gap-3 text-[11px] font-medium uppercase tracking-wider text-slate-400">
              <span className="h-px flex-1 bg-slate-200" /> ou continuer avec <span className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {["Google", "Apple"].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => toast.info(`La connexion avec ${p} arrive bientôt.`)}
                  className="rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:border-bleu/40 hover:text-bleu"
                  data-testid={`auth-social-${p.toLowerCase()}`}
                >
                  {p}
                </button>
              ))}
            </div>

            <p className="mt-6 text-center text-[11px] leading-relaxed text-slate-400">
              En continuant, vous acceptez nos <Link to="/conditions" className="font-semibold text-bleu underline">CGU</Link> et notre <Link to="/confidentialite" className="font-semibold text-bleu underline">politique de confidentialité</Link>.
            </p>
            <Link to="/devenir-partenaire" data-testid="auth-partner-link"
              className="group relative mt-6 block overflow-hidden rounded-2xl bg-gradient-to-r from-bleu-night via-bleu-dark to-bleu p-4 shadow-lg shadow-bleu/20 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-bleu/30 sm:p-5">
              <span className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-bleu-accent via-white to-rouge" />
              <span className="pointer-events-none absolute -right-10 -top-12 h-32 w-32 rounded-full bg-white/10 blur-2xl transition-transform duration-500 group-hover:scale-150" />
              <div className="relative flex items-center gap-3.5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15">
                  <Handshake className="h-5 w-5 text-white" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-bleu-accent">Espace partenaires</p>
                  <p className="mt-0.5 text-[13px] font-extrabold leading-snug text-white">
                    Vous êtes <span className="text-amber-300">avocat</span>, <span className="text-amber-300">association</span>, <span className="text-amber-300">centre de formation</span> ou <span className="text-amber-300">recruteur</span> ?
                  </p>
                </div>
                <span className="hidden shrink-0 items-center gap-1.5 rounded-full bg-white px-4 py-2 text-[11px] font-extrabold text-bleu transition-transform duration-300 group-hover:translate-x-0.5 sm:inline-flex">
                  Devenir partenaire <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
              <span className="relative mt-2.5 flex items-center justify-center gap-1.5 rounded-full bg-white py-2 text-[11px] font-extrabold text-bleu sm:hidden">
                Devenir partenaire <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
